import type { ReactNode } from 'react';
import type { AdminSearchResultItem } from '../types';
import { createContext, useContext, useMemo, useState } from 'react';
import { useQueryParam } from '~/hooks/useQueryParam'; // Export directly instead of from index to avoid circular dependency
import { useDebouncedValue } from '~/hooks/useDebouncedValue'; // Export directly instead of from index to avoid circular dependency
import { useAdminSearchResults } from '../api/useAdminSearchResults';
import { AdminSearchParamOptions, ALGOLIA_SEARCH_DEBOUNCE_MS } from '../constants';

export type AdminSearchContextValue = {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: AdminSearchResultItem[];
  isLoading: boolean;
  isError: boolean;
  errorStatus?: number;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  totalCount: number;
  currentItemInItemPane: any;
  setCurrentItemInItemPane: (item: any) => void;
};

type AdminSearchProviderProps = {
  children: ReactNode;
};

export const AdminSearchContext = createContext<AdminSearchContextValue | null>(null);

export const AdminSearchProvider = ({ children }: AdminSearchProviderProps) => {
  const [searchQuery, setSearchQuery] = useQueryParam<string>(AdminSearchParamOptions.QUERY, '');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, ALGOLIA_SEARCH_DEBOUNCE_MS);
  const [currentItemInItemPane, setCurrentItemInItemPane] = useState<any>(null);
  const isEmpty = !debouncedSearchQuery.trim();

  const searchApiResult = useAdminSearchResults(
    { query: debouncedSearchQuery },
    !isEmpty, // Only enabled when there is a non-empty debounced search query
  );

  const value = useMemo(() => {
    // Use first page for metadata (totals should be consistent across all pages)
    const firstPage = searchApiResult.data?.pages?.[0];
    const folderTotal = firstPage?.pagination?.folders?.total ?? 0;
    const contentTotal = firstPage?.pagination?.content?.total ?? 0;
    const totalCount = folderTotal + contentTotal;

    return {
      searchQuery,
      setSearchQuery,
      searchResults: searchApiResult.data?.pages?.map((page) => page.data).flat() || [],
      isLoading: searchApiResult.isLoading && !isEmpty, // When the query is empty, we don't want to show loading state (transtack query return true here even though it's not loading)
      isError: searchApiResult.isError,
      errorStatus: searchApiResult?.error?.status,
      hasNextPage: searchApiResult.hasNextPage,
      fetchNextPage: searchApiResult.fetchNextPage,
      isFetchingNextPage: searchApiResult.isFetchingNextPage,
      totalCount,
      currentItemInItemPane,
      setCurrentItemInItemPane,
    };
  }, [searchQuery, setSearchQuery, searchApiResult, currentItemInItemPane, setCurrentItemInItemPane, isEmpty]);

  return <AdminSearchContext.Provider value={value}>{children}</AdminSearchContext.Provider>;
};

export const useAdminSearch = () => {
  const context = useContext(AdminSearchContext);
  if (!context) {
    throw new Error('useAdminSearch must be used within an AdminSearchProvider');
  }
  return context;
};
